import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils/utils';

// Types are centralized per project rules
import type {
  ToastAction as ToastActionType,
  ToastComponentProps,
  ToasterToast,
  ToastInput,
  ToastState,
  UseToastReturn,
} from '@/types/ui/toast';
import { TOAST_ACTION_TYPES } from '@/types/ui/toast';

export const ToastProvider = ToastPrimitives.Provider;

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed right-0 top-0 z-[100] flex max-h-screen w-full flex-col p-4 md:max-w-[420px]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-3 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        destructive: 'border-red-500 bg-red-50 text-red-900 dark:border-red-600 dark:bg-red-900/20 dark:text-red-100',
        success:
          'border-green-500 bg-green-50 text-green-900 dark:border-green-600 dark:bg-green-900/20 dark:text-green-100',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.success]:text-green-600 group-[.destructive]:hover:text-red-50 group-[.success]:hover:text-green-800 group-[.destructive]:focus:ring-red-400 group-[.success]:focus:ring-green-400 group-[.destructive]:focus:ring-offset-red-600 dark:group-[.success]:text-green-400 dark:group-[.success]:hover:text-green-200',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// Export ancillary type aliases for compatibility
export type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
export type ToastActionElement = React.ReactElement<typeof ToastAction>;

const TOAST_LIMIT = 1;
// Centralized visible duration for each toast (in ms)
const TOAST_DURATION_MS = 2000;
const TOAST_REMOVE_DELAY = 1000000;

let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return;
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: TOAST_ACTION_TYPES.REMOVE_TOAST, toastId });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: ToastState, action: ToastActionType): ToastState => {
  switch (action.type) {
    case TOAST_ACTION_TYPES.ADD_TOAST:
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
    case TOAST_ACTION_TYPES.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t: ToasterToast) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };
    case TOAST_ACTION_TYPES.DISMISS_TOAST: {
      const { toastId } = action;
      if (toastId) addToRemoveQueue(toastId);
      else state.toasts.forEach((t) => addToRemoveQueue(t.id));
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t,
        ),
      };
    }
    case TOAST_ACTION_TYPES.REMOVE_TOAST:
      if (action.toastId === undefined) return { ...state, toasts: [] };
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) };
  }
};

const listeners: Array<(state: ToastState) => void> = [];
let memoryState: ToastState = { toasts: [] };

function dispatch(action: ToastActionType) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((l) => l(memoryState));
}

export function toast({ ...props }: ToastInput) {
  const id = genId();
  const update = (payload: Partial<ToasterToast>) =>
    dispatch({ type: TOAST_ACTION_TYPES.UPDATE_TOAST, toast: { ...payload, id } });
  const dismiss = () => dispatch({ type: TOAST_ACTION_TYPES.DISMISS_TOAST, toastId: id });

  dispatch({
    type: TOAST_ACTION_TYPES.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss();
      },
    },
  });

  return { id, dismiss, update };
}

export function useToast(): UseToastReturn {
  const [state, setState] = React.useState<ToastState>(memoryState);
  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: TOAST_ACTION_TYPES.DISMISS_TOAST, toastId }),
  };
}

// Simple helper API for success/error/default toasts
export const simpleToast = {
  success: (message: string, title = 'Success') =>
    toast({ title, description: message, variant: 'success', open: true }),
  error: (message: string, title = 'Error') =>
    toast({ title, description: message, variant: 'destructive', open: true }),
  info: (message: string, title = 'Notification') =>
    toast({ title, description: message, variant: 'default', open: true }),
};

export function Toaster() {
  const { toasts } = useToast();
  return (
    <ToastProvider duration={TOAST_DURATION_MS}>
      {toasts.map(function (t: ToasterToast) {
        const { id, title, description, action, ...props } = t;
        return (
          <Toast key={id} {...(props as ToastProps)}>
            <div className="grid gap-1">
              {description && <ToastDescription>{description}</ToastDescription>}
            </div>
            {action as ToastActionElement}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}

export function ToastComponent({
  autoClose = TOAST_DURATION_MS,
  description,
  open = false,
  title,
  variant = 'default',
}: ToastComponentProps) {
  const { toast } = useToast();
  React.useEffect(() => {
    if (!open) return;
    const toastTitle = title || getDefaultTitle(variant);
    const toastDescription = description || getDefaultDescription(variant);
    const instance = toast({
      title: toastTitle,
      description: toastDescription,
      variant,
      open: true,
    });
    if (autoClose && autoClose > 0) {
      const timer = setTimeout(() => instance.dismiss(), autoClose);
      return () => clearTimeout(timer);
    }
  }, [open, title, description, variant, autoClose, toast]);
  return null;
}

function getDefaultTitle(variant: 'success' | 'destructive' | 'default'): string {
  switch (variant) {
    case 'success':
      return 'Success!';
    case 'destructive':
      return 'Error!';
    default:
      return 'Notification';
  }
}
function getDefaultDescription(variant: 'success' | 'destructive' | 'default'): string {
  switch (variant) {
    case 'success':
      return 'Your task completed successfully 🎉';
    case 'destructive':
      return 'Something went wrong ✌️';
    default:
      return 'Here is your notification';
  }
}

export function ToastEventBridge() {
  const { toast } = useToast();
  const currentToastRef = React.useRef<null | {
    id: string;
    update: (p: Partial<ToasterToast>) => void;
    dismiss: () => void;
  }>(null);
  const lastShownAtRef = React.useRef<number>(0);
  // Keep this longer than the error toast cooldown (currently 1500ms)
  // so that near-consecutive errors update the same toast without re-creating it.
  const UPDATE_WINDOW_MS = 6000;

  React.useEffect(() => {
    const w = window as unknown as { __AP_TOAST_BRIDGE_ACTIVE__?: boolean };
    let attachedByThisInstance = false;
    if (!w.__AP_TOAST_BRIDGE_ACTIVE__) {
      w.__AP_TOAST_BRIDGE_ACTIVE__ = true;
      attachedByThisInstance = true;
    }
    if (!attachedByThisInstance) return;

    const onError = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const now = Date.now();
      // Avoid toggling `open` during updates to prevent flicker.
      // Only set `open: true` when creating a new toast instance.
      const basePayload = {
        title: detail.title ?? 'Error',
        description: detail.description ?? 'Something went wrong',
        variant: (detail.variant as 'destructive' | 'success' | 'default') ?? 'destructive',
      } as const;
      if (currentToastRef.current && now - lastShownAtRef.current < UPDATE_WINDOW_MS) {
        currentToastRef.current.update(basePayload);
      } else {
        // Create a new error toast instance; subsequent errors within UPDATE_WINDOW_MS will update this one
        currentToastRef.current = toast({ ...basePayload, open: true });
      }
      lastShownAtRef.current = now;
    };

    const onSuccess = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const now = Date.now();
      // Mirror error behavior: do not toggle `open` on updates
      const basePayload = {
        title: detail.title ?? 'Success',
        description: detail.description ?? 'Operation completed successfully.',
        variant: (detail.variant as 'destructive' | 'success' | 'default') ?? 'success',
      } as const;
      if (currentToastRef.current && now - lastShownAtRef.current < UPDATE_WINDOW_MS) {
        currentToastRef.current.update(basePayload);
      } else {
        currentToastRef.current = toast({ ...basePayload, open: true });
      }
      lastShownAtRef.current = now;
    };

    window.addEventListener('showErrorToast', onError as EventListener);
    window.addEventListener('showSuccessToast', onSuccess as EventListener);
    return () => {
      if (!attachedByThisInstance) return;
      window.removeEventListener('showErrorToast', onError as EventListener);
      window.removeEventListener('showSuccessToast', onSuccess as EventListener);
      w.__AP_TOAST_BRIDGE_ACTIVE__ = false;
    };
  }, [toast]);
  return null;
}
